import { X, CheckCircle2, AlertTriangle, AlertCircle, Info, type LucideProps } from 'lucide-react'
import { cva, type VariantProps } from 'class-variance-authority'
import { type ForwardRefExoticComponent, type RefAttributes } from 'react'
import { cn } from '@/lib/utils'
import { useToasts, toastStore, type ToastVariant } from './useNotify'

const toastVariants = cva(
  'pointer-events-auto flex items-start gap-3 rounded-md border bg-popover p-3 shadow-md w-80',
  {
    variants: {
      variant: {
        default: 'border-border text-foreground',
        success: 'border-success bg-success/10 text-foreground',
        error: 'border-destructive bg-destructive/10 text-foreground',
        warning: 'border-warning bg-warning/10 text-foreground',
      },
    },
    defaultVariants: { variant: 'default' },
  },
)

type LucideIcon = ForwardRefExoticComponent<Omit<LucideProps, 'ref'> & RefAttributes<SVGSVGElement>>

const ICONS: Record<ToastVariant, LucideIcon> = {
  default: Info,
  success: CheckCircle2,
  error: AlertCircle,
  warning: AlertTriangle,
}

export interface ToasterProps extends VariantProps<typeof toastVariants> {
  className?: string
}

export function Toaster({ className }: ToasterProps) {
  const toasts = useToasts()
  const hasError = toasts.some(t => t.variant === 'error')

  return (
    <div
      role="status"
      aria-live={hasError ? 'assertive' : 'polite'}
      aria-atomic="true"
      className={cn('pointer-events-none fixed bottom-4 right-4 z-50 flex flex-col gap-2', className)}
    >
      {toasts.map(t => {
        const variant = t.variant ?? 'default'
        const Icon = ICONS[variant]
        return (
          <div key={t.id} className={toastVariants({ variant })}>
            <Icon className="h-4 w-4 mt-0.5 shrink-0" aria-hidden />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold">{t.title}</div>
              {t.description && <div className="text-xs text-muted-foreground mt-0.5">{t.description}</div>}
            </div>
            <button
              onClick={() => toastStore.remove(t.id)}
              aria-label="Dismiss"
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )
      })}
    </div>
  )
}
